import { useBrokerContext } from '@/app/context';
import { CommercializationPercentFields } from '@/features/quotes/components/commercialization-percent-fields.component';
import {
  useGroupLifeQuote,
  type GroupLifeCoBroker,
} from '@/features/quotes/context/group-life-quote.context';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import { useSaveGroupLifeStep } from '@/features/quotes/hooks/group-life/use-save-step.hook';
import {
  parsePercent,
  validateAgencing,
  validateBrokerage,
  validateCommercialLimit,
  type CommercializationPercentValues,
} from '@/features/quotes/schemas/commercialization-percent.schema';
import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { useAdvisoryOptions } from '@/shared/utils';
import {
  Alert,
  Button,
  Checkbox,
  Field,
  Select,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { ArrowLeft, ArrowRight, Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type Props = {
  onNext: () => void;
  onBack: () => void;
  isViewMode?: boolean;
};

type CoBrokerRow = {
  id: string;
  coBrokerId: string;
  coBrokerName: string;
  participationPercent: string;
  registerNumber: string;
  susepCode: string;
};

/** Primeiro erro do campo, normalizado para string (o validator devolve string). */
function firstError(errors?: Array<unknown>): string | undefined {
  const error = errors?.[0];
  if (!error) return undefined;
  return typeof error === 'string'
    ? error
    : (error as { message?: string }).message;
}

function parsePercentageInput(value: string) {
  const normalized = value.replace(',', '.').trim();
  if (!normalized) return NaN;
  return Number(normalized);
}

function sanitizePercentageInput(value: string) {
  let hasSeparator = false;
  return value
    .replace(/\s/g, '')
    .split('')
    .filter(char => {
      if (/\d/.test(char)) return true;
      if ((char === ',' || char === '.') && !hasSeparator) {
        hasSeparator = true;
        return true;
      }
      return false;
    })
    .join('');
}

/** Participação é percentual: o campo não aceita valor acima de 100. */
function clampParticipationInput(value: string) {
  const sanitized = sanitizePercentageInput(value);
  if (!sanitized || sanitized === ',' || sanitized === '.') return sanitized;
  const percent = parsePercentageInput(sanitized);
  if (!Number.isFinite(percent) || percent <= 100) return sanitized;
  return '100';
}

function participationTotal(leader: string, rows: CoBrokerRow[]) {
  const leaderPercent = parsePercentageInput(leader);
  const leaderValue = Number.isFinite(leaderPercent) ? leaderPercent : 0;
  return rows.reduce((sum, row) => {
    const percent = parsePercentageInput(row.participationPercent);
    return sum + (Number.isFinite(percent) ? percent : 0);
  }, leaderValue);
}

function formatParticipationTotal(total: number) {
  return total.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function normalizeCoBrokerList(coBrokers: CoBrokerRow[]) {
  const seen = new Set<string>();
  return coBrokers.filter(coBroker => {
    const identifier = (coBroker.coBrokerId || coBroker.coBrokerName)
      .trim()
      .toLowerCase();
    if (!identifier || seen.has(identifier)) return false;
    seen.add(identifier);
    return true;
  });
}

function asText(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function rowsFromSaved(coBrokers?: GroupLifeCoBroker[]): CoBrokerRow[] {
  return (coBrokers ?? []).map((coBroker, index) => ({
    id: `${coBroker.agentCode}-${index}`,
    coBrokerId: coBroker.agentCode,
    coBrokerName: coBroker.name,
    participationPercent: coBroker.sharePercent,
    registerNumber: coBroker.registerNumber ?? '',
    susepCode: coBroker.susepCode ?? '',
  }));
}

export function StepCommercialization({
  onNext,
  onBack,
  isViewMode = false,
}: Props) {
  const { commercialization, setCommercialization } = useGroupLifeQuote();
  const { productCode } = useQuoteParams();
  const { selectedBroker } = useBrokerContext();
  const initialValues: CommercializationPercentValues = {
    brokeragePercent: commercialization?.brokeragePercent ?? '',
    agencingPercent: commercialization?.agencingPercent ?? '',
  };
  const form = useQuoteForm({ defaultValues: initialValues });
  const { save, saving, error: saveError } = useSaveGroupLifeStep();
  const busy = saving;

  const savedRows = rowsFromSaved(commercialization?.coBrokers);
  const [includeCoBrokerage, setIncludeCoBrokerage] = useState(
    savedRows.length > 0
  );
  const [participationPercent, setParticipationPercent] = useState(
    savedRows.length > 0
      ? commercialization?.leaderSharePercent || ''
      : '100,00'
  );
  const [coBrokers, setCoBrokers] = useState<CoBrokerRow[]>(savedRows);
  const [coBrokerErrors, setCoBrokerErrors] = useState<
    Record<string, { coBroker?: string; participationPercent?: string }>
  >({});
  const [participationSumError, setParticipationSumError] = useState<
    string | null
  >(null);
  const hydrated = useRef(savedRows.length > 0);

  const {
    items: brokerOptions,
    isLoading: isLoadingBrokers,
    brokerByValue,
  } = useAdvisoryOptions({
      search: '',
      pageSize: 1000,
      partyType: 'broker',
      brokerId:
        selectedBroker?.entityId ||
        selectedBroker?.partyId ||
        selectedBroker?.id,
      productCode,
    });

  useEffect(() => {
    if (hydrated.current) return;
    const rows = rowsFromSaved(commercialization?.coBrokers);
    if (!commercialization) return;
    hydrated.current = true;
    if (rows.length === 0) return;
    setIncludeCoBrokerage(true);
    setCoBrokers(rows);
    setParticipationPercent(commercialization.leaderSharePercent || '');
  }, [commercialization]);

  useEffect(() => {
    if (!includeCoBrokerage && participationPercent !== '100,00') {
      setParticipationPercent('100,00');
    }
  }, [includeCoBrokerage, participationPercent]);

  useEffect(() => {
    if (!includeCoBrokerage) {
      setParticipationSumError(null);
      return;
    }
    const total = participationTotal(participationPercent, coBrokers);
    if (total > 100.01) {
      setParticipationSumError(
        `A soma dos percentuais de participação não pode ser maior que 100%. Total atual: ${formatParticipationTotal(total)}%`
      );
      return;
    }
    setParticipationSumError(current =>
      current?.includes('não pode ser maior') ? null : current
    );
  }, [includeCoBrokerage, participationPercent, coBrokers]);

  const leaderCodes = new Set(
    [
      selectedBroker?.code,
      selectedBroker?.partyId,
      selectedBroker?.entityId,
      selectedBroker?.id,
    ]
      .map(value => asText(value).toLowerCase())
      .filter(Boolean)
  );
  const leaderName = asText(selectedBroker?.name).toLowerCase();
  const advisoryBrokerOptions = brokerOptions.filter(item => {
    const value = asText(item.value).toLowerCase();
    const label = asText(item.label).toLowerCase();
    if (value && leaderCodes.has(value)) return false;
    if (leaderName && label === leaderName) return false;
    return true;
  });

  const optionsForRow = (rowId: string) => {
    const taken = new Set(
      coBrokers
        .filter(row => row.id !== rowId && row.coBrokerId)
        .map(row => asText(row.coBrokerId).toLowerCase())
        .filter(Boolean)
    );
    return advisoryBrokerOptions.filter(
      item => !taken.has(asText(item.value).toLowerCase())
    );
  };

  const remember = (rows: CoBrokerRow[], included: boolean, share: string) => {
    setCommercialization({
      brokeragePercent: form.state.values.brokeragePercent,
      agencingPercent: form.state.values.agencingPercent,
      agencyPercent: commercialization?.agencyPercent ?? '',
      surchargePercent: commercialization?.surchargePercent,
      actuarialDiscountPercent: commercialization?.actuarialDiscountPercent,
      leaderSharePercent: included ? share : '',
      coBrokers: included
        ? rows.map(row => ({
            agentCode: row.coBrokerId,
            name: row.coBrokerName,
            sharePercent: row.participationPercent,
            registerNumber: row.registerNumber || undefined,
            susepCode: row.susepCode || undefined,
          }))
        : [],
    });
  };

  const handleBack = () => {
    remember(coBrokers, includeCoBrokerage, participationPercent);
    onBack();
  };

  const handleToggle = (checked: boolean) => {
    setParticipationSumError(null);
    setIncludeCoBrokerage(checked);
    if (checked && coBrokers.length === 0) {
      const row: CoBrokerRow = {
        id: Date.now().toString(),
        coBrokerId: '',
        coBrokerName: '',
        participationPercent: '',
        registerNumber: '',
        susepCode: '',
      };
      setCoBrokers([row]);
      return;
    }
    if (!checked) {
      setCoBrokers([]);
      setCoBrokerErrors({});
    }
  };

  const handleAddCoBroker = () => {
    setCoBrokers(prev => [
      ...prev,
      {
        id: Date.now().toString(),
        coBrokerId: '',
        coBrokerName: '',
        participationPercent: '',
        registerNumber: '',
        susepCode: '',
      },
    ]);
  };

  const handleRemoveCoBroker = (idToRemove: string) => {
    const next = coBrokers.filter(row => row.id !== idToRemove);
    setCoBrokers(next);
    setCoBrokerErrors(prev => {
      const errors = { ...prev };
      delete errors[idToRemove];
      return errors;
    });
    if (next.length === 0) setIncludeCoBrokerage(false);
  };

  const handleCoBrokerSelection = (id: string, value: string) => {
    const option = brokerOptions.find(item => item.value === value);
    const record = brokerByValue.get(value);
    setCoBrokers(prev =>
      prev.map(row =>
        row.id === id
          ? {
              ...row,
              coBrokerId: value,
              coBrokerName: option?.label || '',
              registerNumber: record?.licenseNumber || '',
              susepCode: record?.susepCode || '',
            }
          : row
      )
    );
    setParticipationSumError(null);
  };

  const handleSubmit = () => {
    void form.validateAllFields('change').then(async fieldErrors => {
      const values = form.state.values;
      const limitError = validateCommercialLimit(
        values.brokeragePercent,
        values.agencingPercent
      );
      const hasFieldErrors = fieldErrors && Object.keys(fieldErrors).length > 0;
      if (hasFieldErrors || limitError || busy) return;

      setParticipationSumError(null);
      let blocked = false;
      const normalized = normalizeCoBrokerList(coBrokers);

      if (includeCoBrokerage) {
        const leaderPercent = parsePercentageInput(participationPercent);
        if (
          !participationPercent.trim() ||
          Number.isNaN(leaderPercent) ||
          leaderPercent <= 0 ||
          leaderPercent > 100
        ) {
          blocked = true;
          setParticipationSumError(
            'Percentual de participação do corretor deve ser maior que 0 e menor ou igual a 100'
          );
        }

        const nextErrors: Record<
          string,
          { coBroker?: string; participationPercent?: string }
        > = {};
        const seen = new Set<string>();
        if (coBrokers.length === 0 || normalized.length === 0) {
          blocked = true;
          setParticipationSumError(
            'Informe ao menos um co-corretor ou desmarque "Incluir co-corretagem".'
          );
        }
        coBrokers.forEach(row => {
          const errors: { coBroker?: string; participationPercent?: string } =
            {};
          const id = row.coBrokerId.trim().toLowerCase();
          const name = row.coBrokerName.trim().toLowerCase();
          if (!row.coBrokerId || !name) {
            errors.coBroker = 'Este campo é obrigatório';
          } else if (
            leaderCodes.has(id) ||
            (leaderName && name === leaderName)
          ) {
            errors.coBroker = 'O corretor líder não pode ser co-corretor';
          } else if (seen.has(id) || (name && seen.has(`name:${name}`))) {
            errors.coBroker = 'Co-corretor já informado';
          }
          if (id) seen.add(id);
          if (name) seen.add(`name:${name}`);
          if (!row.participationPercent.trim()) {
            errors.participationPercent = 'Este campo é obrigatório';
          } else {
            const percent = parsePercentageInput(row.participationPercent);
            if (Number.isNaN(percent) || percent <= 0 || percent > 100) {
              errors.participationPercent =
                'Percentual deve ser maior que 0 e menor ou igual a 100';
            }
          }
          if (Object.keys(errors).length > 0) nextErrors[row.id] = errors;
        });
        if (Object.keys(nextErrors).length > 0) {
          setCoBrokerErrors(nextErrors);
          blocked = true;
        } else {
          setCoBrokerErrors({});
        }
      }

      const filtered = includeCoBrokerage
        ? normalized.filter(row => {
            const id = row.coBrokerId.trim().toLowerCase();
            const name = row.coBrokerName.trim().toLowerCase();
            return (
              !leaderCodes.has(id) &&
              name !== leaderName &&
              parsePercentageInput(row.participationPercent) > 0
            );
          })
        : [];

      if (!blocked && includeCoBrokerage) {
        const leader = parsePercentageInput(participationPercent);
        const leaderValue = Number.isNaN(leader) ? 0 : leader;
        const coSum = filtered.reduce((sum, row) => {
          const percent = parsePercentageInput(row.participationPercent);
          return sum + (Number.isNaN(percent) ? 0 : percent);
        }, 0);
        const total = leaderValue + coSum;
        if (total > 100.01) {
          setParticipationSumError(
            `A soma dos percentuais de participação não pode ser maior que 100%. Total atual: ${formatParticipationTotal(total)}%`
          );
          blocked = true;
        } else if (Math.abs(total - 100) > 0.01) {
          setParticipationSumError(
            `A soma dos percentuais de participação deve ser 100%. Total atual: ${formatParticipationTotal(total)}%`
          );
          blocked = true;
        }
      }

      if (blocked) return;

      const agencyPercent = commercialization?.agencyPercent ?? '';
      const savedCoBrokers: GroupLifeCoBroker[] = includeCoBrokerage
        ? filtered.map(row => ({
            agentCode: row.coBrokerId,
            name: row.coBrokerName,
            sharePercent: row.participationPercent,
            registerNumber: row.registerNumber || undefined,
            susepCode: row.susepCode || undefined,
          }))
        : [];
      setCommercialization({
        brokeragePercent: values.brokeragePercent,
        agencingPercent: values.agencingPercent,
        agencyPercent,
        surchargePercent: commercialization?.surchargePercent,
        actuarialDiscountPercent: commercialization?.actuarialDiscountPercent,
        leaderSharePercent: includeCoBrokerage ? participationPercent : '',
        coBrokers: savedCoBrokers,
      });

      const payload: Record<string, unknown> = {
        brokeragePercent: parsePercent(values.brokeragePercent),
        coBrokers: includeCoBrokerage
          ? filtered.map(row => ({
              agentCode: row.coBrokerId,
              name: row.coBrokerName,
              sharePercent: parsePercentageInput(row.participationPercent),
              ...(row.registerNumber
                ? { registerNumber: row.registerNumber }
                : {}),
            }))
          : [],
      };
      if (values.agencingPercent) {
        payload.agencyingPercent = parsePercent(values.agencingPercent);
      }
      if (includeCoBrokerage) {
        payload.leaderSharePercent = parsePercentageInput(participationPercent);
      }

      const ok = await save('commercialization', payload);
      if (!ok) return;
      onNext();
    });
  };

  return (
    <div className='space-y-6'>
      <Text className='text-lg font-bold text-alba-black-500'>
        Comercialização
      </Text>

      <form.Subscribe
        selector={state => state.values}
        children={values => (
          <form.AppField
            name='brokeragePercent'
            validators={{ onChange: ({ value }) => validateBrokerage(value) }}
            children={brokerageField => (
              <form.AppField
                name='agencingPercent'
                validators={{
                  onChange: ({ value }) => validateAgencing(value),
                }}
                children={agencingField => (
                  <CommercializationPercentFields
                    brokerageValue={brokerageField.state.value}
                    agencingValue={agencingField.state.value}
                    onBrokerageChange={brokerageField.handleChange}
                    onAgencingChange={agencingField.handleChange}
                    brokerageError={firstError(
                      brokerageField.state.meta.errors
                    )}
                    agencingError={firstError(agencingField.state.meta.errors)}
                    limitError={validateCommercialLimit(
                      values.brokeragePercent,
                      values.agencingPercent
                    )}
                    disabled={isViewMode}
                    autoFocusBrokerage={!isViewMode}
                  />
                )}
              />
            )}
          />
        )}
      />

      <RequiredFieldTooltip active>
        <Field.Root
          invalid={Boolean(
            participationSumError?.includes('não pode ser maior')
          )}
        >
          <Field.Label>
            Percentual de Participação
            <span className='text-alba-error-red-500'>*</span>
          </Field.Label>
          <Field.Input
            type='text'
            inputMode='decimal'
            value={includeCoBrokerage ? participationPercent : '100,00'}
            onChange={event => {
              setParticipationPercent(
                clampParticipationInput(event.target.value)
              );
            }}
            placeholder='% 100,00'
            disabled={isViewMode || !includeCoBrokerage}
          />
        </Field.Root>
      </RequiredFieldTooltip>

      <Checkbox.Root
        checked={includeCoBrokerage}
        disabled={isViewMode}
        onCheckedChange={() => {}}
        onClick={() => {
          if (isViewMode) return;
          handleToggle(!includeCoBrokerage);
        }}
        className='cursor-pointer'
      >
        <Checkbox.Control className='cursor-pointer'>
          <Checkbox.Indicator />
        </Checkbox.Control>
        <Checkbox.Label className='cursor-pointer'>
          Incluir co-corretagem?
        </Checkbox.Label>
      </Checkbox.Root>

      {includeCoBrokerage && (
        <div className='flex flex-col gap-4'>
          {coBrokers.map(coBroker => {
            const rowOptions = optionsForRow(coBroker.id);
            return (
            <div
              key={coBroker.id}
              className='grid grid-cols-1 items-end gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]'
            >
              <Field.Root
                className='min-w-0'
                invalid={Boolean(coBrokerErrors[coBroker.id]?.coBroker)}
              >
                <Field.Label className='mb-2'>
                  Co-corretor
                  <span className='text-alba-error-red-500'>*</span>
                </Field.Label>
                <Select.Root
                  value={coBroker.coBrokerId ? [coBroker.coBrokerId] : []}
                  onValueChange={details =>
                    handleCoBrokerSelection(
                      coBroker.id,
                      details.value[0] || ''
                    )
                  }
                  positioning={{ sameWidth: true }}
                  items={rowOptions}
                  disabled={isViewMode}
                >
                  <Select.Control>
                    <Select.Trigger className='min-w-0'>
                      <Select.ValueText
                        placeholder={
                          isLoadingBrokers
                            ? 'Carregando corretores...'
                            : 'Selecione o co-corretor'
                        }
                        className='min-w-0 truncate'
                      />
                      <Select.Indicator />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {rowOptions.map(item => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
                {coBrokerErrors[coBroker.id]?.coBroker && (
                  <Field.ErrorText>
                    {coBrokerErrors[coBroker.id].coBroker}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root
                className='min-w-0'
                invalid={Boolean(
                  coBrokerErrors[coBroker.id]?.participationPercent ||
                    participationSumError?.includes('não pode ser maior')
                )}
              >
                <Field.Label className='mb-2'>
                  Percentual de Participação
                  <span className='text-alba-error-red-500'>*</span>
                </Field.Label>
                <Field.Input
                  type='text'
                  inputMode='decimal'
                  value={coBroker.participationPercent}
                  onChange={event => {
                    const value = clampParticipationInput(event.target.value);
                    setCoBrokers(prev =>
                      prev.map(row =>
                        row.id === coBroker.id
                          ? { ...row, participationPercent: value }
                          : row
                      )
                    );
                    setCoBrokerErrors(prev => {
                      const current = prev[coBroker.id];
                      if (!current?.participationPercent) return prev;
                      const next = { ...current };
                      delete next.participationPercent;
                      return { ...prev, [coBroker.id]: next };
                    });
                  }}
                  placeholder='% XXX'
                  disabled={isViewMode}
                />
                {coBrokerErrors[coBroker.id]?.participationPercent && (
                  <Field.ErrorText>
                    {coBrokerErrors[coBroker.id].participationPercent}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <button
                type='button'
                onClick={() => handleRemoveCoBroker(coBroker.id)}
                className='flex h-12 shrink-0 items-center gap-1 text-sm text-alba-gray-600 hover:text-alba-error-red-500'
                disabled={isViewMode}
                aria-label='Remover co-corretor'
              >
                <Trash2 className='h-4 w-4' strokeWidth={1.6} />
                Remover
              </button>
            </div>
            );
          })}

          <button
            type='button'
            onClick={handleAddCoBroker}
            className='flex items-center gap-2 text-sm font-medium text-alba-purple-500 hover:text-alba-purple-600'
            disabled={isViewMode}
          >
            <Plus className='h-4 w-4' strokeWidth={1.6} />
            Adicionar Co-corretor
          </button>
        </div>
      )}

      {participationSumError && (
        <Alert.Root status='error'>
          <Alert.Icon />
          <Alert.Content>
            <Alert.Description>{participationSumError}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}

      {saveError ? (
        <div className='rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>
          {saveError}
        </div>
      ) : null}

      <div className='flex justify-between border-t border-alba-gray-200 pt-6'>
        <Button variant='ghost' size='large' onClick={handleBack} disabled={busy}>
          <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <Button
          variant='primary'
          size='large'
          onClick={handleSubmit}
          disabled={isViewMode || busy}
        >
          {busy ? 'Salvando…' : 'Próximo'}
          {busy ? (
            <Loader2 className='w-5 h-5 animate-spin' strokeWidth={1.6} />
          ) : (
            <ArrowRight className='w-5 h-5' strokeWidth={1.6} />
          )}
        </Button>
      </div>
    </div>
  );
}
